// How loudly Kinmantle presents each NWS alert. Pure functions shared by the
// Worker (tests) and the wall (banner, Alerts screen, automatic switching).

export type AlertLevel = "takeover" | "warning" | "watch" | "advisory" | "statement";
export type AlertKind = "fire" | "tornado" | "storm" | "flood" | "winter" | "wind" | "heat" | "cold" | "air" | "dust" | "civil" | "other";

/**
 * Life-threatening, act-now alerts: the wall switches to the Alerts screen for
 * these. Kept deliberately short so the takeover stays meaningful.
 */
const TAKEOVER = new Set([
  "Tornado Warning",
  "Tornado Emergency",
  "Severe Thunderstorm Warning",
  "Flash Flood Warning",
  "Flash Flood Emergency",
  "Extreme Wind Warning",
  "Blizzard Warning",
  "Ice Storm Warning",
  "Snow Squall Warning",
  "Dust Storm Warning",
  "Red Flag Warning",
  "Fire Warning",
  "Extreme Fire Danger",
  "Evacuation Immediate",
  "Shelter In Place Warning",
  "Civil Danger Warning",
  "Civil Emergency Message",
  "Local Area Emergency",
  "Law Enforcement Warning",
  "Extreme Heat Warning",
  "Extreme Cold Warning",
  "Hurricane Warning",
  "Tropical Storm Warning",
  "Storm Surge Warning",
  "Tsunami Warning",
  "Avalanche Warning",
]);

export function alertKind(event: string): AlertKind {
  const e = event.toLowerCase();
  if (/red flag|fire/.test(e)) return "fire";
  if (/tornado/.test(e)) return "tornado";
  if (/air quality|air stagnation|smoke/.test(e)) return "air";
  if (/dust/.test(e)) return "dust";
  if (/flood|hydrologic/.test(e)) return "flood";
  if (/thunderstorm|hurricane|tropical|storm surge|marine/.test(e)) return "storm";
  if (/blizzard|winter|snow|ice|freez|frost|sleet|avalanche/.test(e)) return "winter";
  if (/extreme cold|wind chill|cold/.test(e)) return "cold";
  if (/heat/.test(e)) return "heat";
  if (/wind/.test(e)) return "wind";
  if (/evacuation|shelter|civil|emergency|law enforcement|911/.test(e)) return "civil";
  return "other";
}

export function alertLevel(a: { event: string; severity: string }): AlertLevel {
  if (TAKEOVER.has(a.event) || a.severity === "Extreme") return "takeover";
  if (/warning|emergency/i.test(a.event)) return "warning";
  if (/watch/i.test(a.event)) return "watch";
  if (/advisory|alert/i.test(a.event)) return "advisory";
  return "statement";
}

const RANK: Record<AlertLevel, number> = { takeover: 0, warning: 1, watch: 2, advisory: 3, statement: 4 };

/** Most urgent first; fire before others at the same level (it's the Front Range's big one). */
export function sortAlerts<T extends { event: string; severity: string }>(alerts: T[]): T[] {
  return [...alerts].sort((a, b) => RANK[alertLevel(a)] - RANK[alertLevel(b)] || Number(alertKind(b.event) === "fire") - Number(alertKind(a.event) === "fire"));
}

/** Shown as a banner on every screen: anything that takes over, any warning, and any fire-weather alert. */
export function isProminent(a: { event: string; severity: string }): boolean {
  const level = alertLevel(a);
  return level === "takeover" || level === "warning" || alertKind(a.event) === "fire";
}

/** Storm-based warnings drawn as outlines on the radar. */
export const MAPPED_WARNINGS = new Set([
  "Tornado Warning",
  "Severe Thunderstorm Warning",
  "Flash Flood Warning",
  "Special Marine Warning",
  "Snow Squall Warning",
  "Extreme Wind Warning",
  "Dust Storm Warning",
  "Tornado Watch",
  "Severe Thunderstorm Watch",
]);

/** Outline colors follow the NWS map conventions so they read as "official". */
export const WARNING_COLORS: Record<string, string> = {
  "Tornado Warning": "#ff1f1f",
  "Severe Thunderstorm Warning": "#ffa500",
  "Flash Flood Warning": "#22c55e",
  "Special Marine Warning": "#ffa500",
  "Snow Squall Warning": "#c71585",
  "Extreme Wind Warning": "#ff8c00",
  "Dust Storm Warning": "#d2b48c",
  "Tornado Watch": "#ffff00",
  "Severe Thunderstorm Watch": "#db7093",
};
